// Selected WhatsApp account (number) for the whole app.
//
// A tenant can connect several numbers. Two different questions hang off that:
//   • "Which conversations am I looking at?"  → `selectedId`, may be 'all'
//   • "Which number sends this?"              → the conversation's own number
//     for replies (server-enforced); the selected/default number for broadcasts
//     and new outbound.
// The choice is remembered per browser so an agent who works one line all day
// doesn't re-pick it on every page load.
import {
  createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from 'react';
import { listIntegrations, numberLabel, type ConnectedNumber } from './whatsappApi';
import { useTenant } from './tenant';

export const ALL_ACCOUNTS = 'all';

export interface AccountsState {
  loading: boolean;
  error: string | null;
  /** Every connected number, default first. */
  numbers: ConnectedNumber[];
  /** The tenant's default "send from" number id, or null when none connected. */
  defaultId: string | null;
  /** What the user picked in the switcher: a number id, or 'all'. */
  selectedId: string;
  setSelectedId: (id: string) => void;
  /** The picked number, or the default when 'all' is selected (for send-from). */
  selected: ConnectedNumber | null;
  /** Convenience: id to send NEW outbound from (never 'all'). */
  sendFromId: string | null;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AccountsState | null>(null);

function storageKey(tenantId: string) {
  return `watag.selectedAccount.${tenantId}`;
}

export function AccountsProvider({ children }: { children: ReactNode }) {
  const { tenantId } = useTenant();
  const [numbers, setNumbers] = useState<ConnectedNumber[]>([]);
  const [defaultId, setDefaultId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedIdState] = useState<string>(ALL_ACCOUNTS);

  const refresh = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await listIntegrations({} as Record<string, never>);
      const sorted = [...r.items].sort((a, b) => (a.id === r.defaultId ? -1 : b.id === r.defaultId ? 1 : 0));
      setNumbers(sorted);
      setDefaultId(r.defaultId);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { void refresh(); }, [refresh]);

  // Restore the remembered choice once we know the tenant; drop it if that
  // number has since been removed.
  useEffect(() => {
    if (!tenantId) return;
    let saved: string | null = null;
    try { saved = localStorage.getItem(storageKey(tenantId)); } catch { /* private mode */ }
    setSelectedIdState(saved || ALL_ACCOUNTS);
  }, [tenantId]);
  useEffect(() => {
    if (loading || selectedId === ALL_ACCOUNTS) return;
    if (!numbers.some((n) => n.id === selectedId)) setSelectedIdState(ALL_ACCOUNTS);
  }, [loading, numbers, selectedId]);

  const setSelectedId = useCallback((id: string) => {
    setSelectedIdState(id);
    if (tenantId) { try { localStorage.setItem(storageKey(tenantId), id); } catch { /* ignore */ } }
  }, [tenantId]);

  const value = useMemo<AccountsState>(() => {
    const picked = numbers.find((n) => n.id === selectedId) ?? null;
    const def = numbers.find((n) => n.id === defaultId) ?? numbers[0] ?? null;
    const selected = picked ?? def;
    return {
      loading, error, numbers, defaultId, selectedId, setSelectedId, selected,
      sendFromId: selected?.id ?? null, refresh,
    };
  }, [loading, error, numbers, defaultId, selectedId, setSelectedId, refresh]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAccounts(): AccountsState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAccounts must be used inside <AccountsProvider>');
  return v;
}

/** Label helper re-exported for convenience in switcher / chips. */
export { numberLabel };
